import { KnownError } from "@/api/client";
import {
  type MessageKind,
  type SupportAttachment,
  type SupportMessage,
  fetchAdminSupportAttachmentBlob,
  useAdminSupportMessageMutation,
  useAdminSupportMessageRetryMutation,
  useAdminSupportTicket,
  useAdminSupportTicketStatusMutation,
} from "@/api/support";
import { cn } from "@/lib/utils";
import { DeliveryStatusChip, TicketStatusChip } from "@/routes/admin/support/components/support-status-chip";
import { triggerBlobDownload } from "@/routes/admin/support/lib/download";
import { formatTicketDateTime } from "@/routes/admin/support/lib/format-datetime";
import {
  TICKET_STATUS_OPTIONS,
  deliveryStatusDisplay,
  ticketStatusDisplay,
} from "@/routes/admin/support/lib/status-display";
import {
  Button,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  DropdownTriggerButton,
  Loading,
  Typography,
  useConfirm,
  useToastMessage,
} from "@backpac/monica";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";

export function SupportTicketDetailPage() {
  const { id } = useParams<{ id: string }>();

  if (id == null) {
    return (
      <Typography size="sm" color="textSecondary">
        Missing ticket id.
      </Typography>
    );
  }

  return <SupportTicketDetailContent ticketId={id} />;
}

function SupportTicketDetailContent({ ticketId }: { ticketId: string }) {
  const { data, isLoading, isError, error, refetch } = useAdminSupportTicket(ticketId);

  return (
    <div className="flex flex-col gap-6 max-w-[820px] mx-auto">
      <Link to="/admin/support" className="inline-flex items-center gap-1 w-fit">
        <i className="i-bp-arrow-left size-3.5 inline-block" />
        <Typography size="xs" color="textMuted">
          Back to inbox
        </Typography>
      </Link>

      {isLoading ? <Loading className="py-16" /> : null}

      {isError ? <SupportDetailErrorState error={error} onRetry={() => refetch()} /> : null}

      {!isLoading && !isError && data != null ? (
        <>
          <TicketSummary ticketId={ticketId} ticket={data.ticket} />
          <MessageThread ticketId={ticketId} messages={data.messages} />
          <MessageComposer ticketId={ticketId} customerEmail={data.ticket.email} />
        </>
      ) : null}
    </div>
  );
}

function SupportDetailErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  // 401 is included here because the backend's staff guard returns a plain 401 for
  // "logged in, but not a staff account" too (see the `request()` comment in api/support.ts);
  // a real 401-with-no-session instead bubbles up to <AuthBoundary /> before it gets here.
  const isForbidden = KnownError.is(error) && (error.status === 403 || error.status === 401);
  const isNotFound = KnownError.is(error) && error.status === 404;
  const message = isForbidden
    ? "You're signed in, but this account isn't authorized to view support tickets."
    : isNotFound
      ? "This ticket doesn't exist or was removed."
      : KnownError.is(error)
        ? error.message
        : "Couldn't load this ticket. Please try again.";

  return (
    <div className="rounded-[16px] bg-colorOrangeLight text-colorOrange p-6 flex items-center justify-between gap-4 flex-wrap">
      <Typography size="sm" className="text-colorOrange">
        {message}
      </Typography>
      {!isForbidden && !isNotFound ? (
        <Button variant="outlined" size="sm" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}

function TicketSummary({
  ticketId,
  ticket,
}: {
  ticketId: string;
  ticket: NonNullable<ReturnType<typeof useAdminSupportTicket>["data"]>["ticket"];
}) {
  const { mutate: updateStatus, isPending } = useAdminSupportTicketStatusMutation(ticketId);
  const { info, warning } = useToastMessage();

  return (
    <div className="bg-white rounded-[16px] border border-borderLight p-6 flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-1">
          <Typography size="lg" weight={600} color="textPrimary">
            {ticket.subject}
          </Typography>
          <Typography size="xs" color="textMuted">
            {ticket.reference} · from {ticket.name} ({ticket.email}) · via {ticket.source}
          </Typography>
          <Typography size="2xs" color="textMuted">
            Created {formatTicketDateTime(ticket.createdAt)} · Updated {formatTicketDateTime(ticket.updatedAt)}
          </Typography>
        </div>

        <Dropdown>
          <DropdownTrigger asChild>
            <DropdownTriggerButton size="sm" disabled={isPending}>
              <TicketStatusChip {...ticketStatusDisplay[ticket.status]} />
            </DropdownTriggerButton>
          </DropdownTrigger>
          <DropdownContent align="end">
            {TICKET_STATUS_OPTIONS.map((status) => (
              <DropdownItem
                key={status}
                disabled={status === ticket.status}
                onSelect={() =>
                  updateStatus(
                    { status },
                    {
                      onSuccess: () => info(`Status changed to ${ticketStatusDisplay[status].label}.`),
                      onError: (mutationError) =>
                        warning(KnownError.is(mutationError) ? mutationError.message : "Couldn't update the status."),
                    },
                  )
                }
              >
                {ticketStatusDisplay[status].label}
              </DropdownItem>
            ))}
          </DropdownContent>
        </Dropdown>
      </div>
    </div>
  );
}

function MessageThread({ ticketId, messages }: { ticketId: string; messages: SupportMessage[] }) {
  if (messages.length === 0) {
    return (
      <div className="rounded-[16px] border border-borderLight bg-white p-8 text-center">
        <Typography size="sm" color="textSecondary">
          No messages yet.
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {messages.map((message) => (
        <MessageBubble key={message.id} ticketId={ticketId} message={message} />
      ))}
    </div>
  );
}

function MessageBubble({ ticketId, message }: { ticketId: string; message: SupportMessage }) {
  const isNote = message.kind === "note";
  const isCustomer = message.kind === "customer";
  const { mutate: retry, isPending: isRetrying } = useAdminSupportMessageRetryMutation(ticketId);
  const { confirm } = useConfirm();
  const { info, warning } = useToastMessage();

  const handleRetry = async () => {
    const confirmed = await confirm({
      title: "Retry sending this reply?",
      description: "We'll attempt delivery again to the customer's email. This won't create a duplicate message.",
      confirmText: "Retry delivery",
      cancelText: "Cancel",
    });
    if (!confirmed) {
      return;
    }
    retry(message.id, {
      onSuccess: () => info("Retry queued."),
      onError: (mutationError) =>
        warning(KnownError.is(mutationError) ? mutationError.message : "Retry failed to queue."),
    });
  };

  return (
    <div
      className={cn(
        "rounded-[16px] p-5 border flex flex-col gap-3",
        isNote
          ? "border-dashed border-colorOrange/40 bg-colorOrangeLight/40"
          : isCustomer
            ? "border-borderLight bg-white"
            : "border-colorBlueLight bg-colorBlueLight/40",
      )}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <MessageKindLabel kind={message.kind} />
          <Typography size="xs" weight={500} color="textPrimary">
            {message.authorName}
          </Typography>
          <Typography size="2xs" color="textMuted">
            {formatTicketDateTime(message.createdAt)}
          </Typography>
        </div>
        {message.deliveryStatus !== "not_applicable" ? (
          <DeliveryStatusChip {...deliveryStatusDisplay[message.deliveryStatus]} />
        ) : null}
      </div>

      {/* Customer message bodies come from an external, untrusted sender:
          rendered as plain text (never HTML), and any URLs inside are shown
          as inert text rather than clickable/auto-linked anchors. */}
      <Typography size="sm" color="textPrimary" className="whitespace-pre-wrap break-words leading-[1.6]">
        {message.body}
      </Typography>

      {message.attachments != null && message.attachments.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          {message.attachments.map((attachment) => (
            <AttachmentRow key={attachment.id} attachment={attachment} />
          ))}
        </div>
      ) : null}

      {message.deliveryStatus === "failed" ? (
        <div className="flex items-center gap-3">
          {message.deliveryError != null ? (
            <Typography size="2xs" className="text-purple-600">
              {message.deliveryError}
            </Typography>
          ) : null}
          <Button variant="outlined" size="sm" onClick={handleRetry} disabled={isRetrying} loading={isRetrying}>
            Retry delivery
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function MessageKindLabel({ kind }: { kind: MessageKind }) {
  if (kind === "note") {
    return (
      <span className="inline-flex items-center gap-1 text-colorOrange">
        <i className="i-bp-info size-3.5 inline-block" />
        <Typography size="2xs" weight={600} className="text-colorOrange uppercase tracking-wide" asChild>
          <span>Internal note (not sent to customer)</span>
        </Typography>
      </span>
    );
  }
  if (kind === "reply") {
    return (
      <Typography size="2xs" weight={600} color="colorBlue" className="uppercase tracking-wide" asChild>
        <span>Staff reply</span>
      </Typography>
    );
  }
  return (
    <Typography size="2xs" weight={600} color="textMuted" className="uppercase tracking-wide" asChild>
      <span>Customer message</span>
    </Typography>
  );
}

export function AttachmentRow({ attachment }: { attachment: SupportAttachment }) {
  const [state, setState] = useState<"idle" | "downloading">("idle");
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Never rendered as a button/link when the backend says the original bytes
  // are gone (`available: false` — the server itself doesn't return a reason
  // string here, just this boolean, so we show a plain honest label rather
  // than inventing a specific cause).
  if (!attachment.available) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <i className="i-bp-file size-3.5 inline-block text-textMuted flex-shrink-0" />
        <Typography size="xs" color="textMuted">
          {attachment.filename}
        </Typography>
        <Typography size="2xs" className="text-colorOrange">
          (not available for download)
        </Typography>
      </div>
    );
  }

  const handleDownload = async () => {
    setState("downloading");
    setDownloadError(null);
    try {
      const blob = await fetchAdminSupportAttachmentBlob(attachment.id);
      await triggerBlobDownload(blob, attachment.filename);
    } catch (error) {
      setDownloadError(KnownError.is(error) ? error.message : "Couldn't download this attachment.");
    } finally {
      setState("idle");
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={handleDownload}
        disabled={state === "downloading"}
        className="inline-flex items-center gap-1.5 text-xs text-colorBlue underline disabled:opacity-60 disabled:no-underline"
      >
        <i
          className={cn(
            "size-3.5 inline-block flex-shrink-0",
            state === "downloading" ? "i-bp-spinner animate-spin" : "i-bp-download",
          )}
        />
        {attachment.filename}
      </button>
      {downloadError != null ? (
        <Typography size="2xs" className="text-colorOrange">
          {downloadError}
        </Typography>
      ) : null}
    </div>
  );
}

export function MessageComposer({ ticketId, customerEmail }: { ticketId: string; customerEmail: string }) {
  const [mode, setMode] = useState<"reply" | "note">("reply");
  const [body, setBody] = useState("");
  // Retained across an ambiguous failed send within this page session so a
  // retry can't create a duplicate reply/note; a fresh id is only minted
  // after a confirmed successful send.
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const { mutateAsync, isPending } = useAdminSupportMessageMutation(ticketId);
  const { info, warning } = useToastMessage();
  const [sendError, setSendError] = useState<string | null>(null);

  const placeholder = useMemo(
    () =>
      mode === "reply"
        ? `Reply to ${customerEmail}. Sent as help@backpac.app.`
        : "Internal note, visible to staff only.",
    [mode, customerEmail],
  );

  const handleSend = async () => {
    const trimmed = body.trim();
    if (trimmed.length === 0) {
      return;
    }
    setSendError(null);
    try {
      await mutateAsync({ body: trimmed, kind: mode, requestId });
      setBody("");
      setRequestId(crypto.randomUUID());
      info(mode === "reply" ? "Reply sent." : "Note added.");
    } catch (error) {
      const message = KnownError.is(error)
        ? error.message
        : "Couldn't send this. If this keeps happening, it's safe to retry.";
      setSendError(message);
      warning(message);
      // requestId intentionally NOT regenerated here: keeps the retry idempotent.
    }
  };

  return (
    <div className="bg-white rounded-[16px] border border-borderLight p-6 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <Button variant={mode === "reply" ? "black" : "outlined"} size="sm" onClick={() => setMode("reply")}>
          Reply to customer
        </Button>
        <Button variant={mode === "note" ? "black" : "outlined"} size="sm" onClick={() => setMode("note")}>
          Internal note
        </Button>
      </div>

      {/* maxLength matches the server's real CreateSupportMessageDto.body cap (Length(1, 10_000) in
          apps/server/src/modules/support/dto/support.dto.ts) so the field can't be typed past what the
          server will actually accept. */}
      <textarea
        className="min-h-[120px] w-full rounded-2xl border border-borderLight bg-transparent px-4 py-3 text-sm focus-visible:border-brandTangerine focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brandTangerine resize-y"
        placeholder={placeholder}
        value={body}
        maxLength={10_000}
        onChange={(event) => setBody(event.target.value)}
      />

      {sendError != null ? (
        <Typography size="xs" className="text-colorOrange">
          {sendError}
        </Typography>
      ) : null}

      <div className="flex items-center justify-between">
        <Typography size="2xs" color="textMuted">
          {mode === "reply"
            ? "Replies always go out from help@backpac.app to the ticket's stored email."
            : "Notes are never emailed."}
        </Typography>
        <Button size="md" onClick={handleSend} disabled={isPending || body.trim().length === 0} loading={isPending}>
          {mode === "reply" ? "Send reply" : "Add note"}
        </Button>
      </div>
    </div>
  );
}
